import React, { useState } from "react";
import "./App.css";

function Badge({ ok, children }) {
  const cls = ok ? "badge badge-ok" : "badge badge-bad";
  return <span className={cls}>{children ?? (ok ? "PASS" : "FAIL")}</span>;
}

function KeyVal({ label, value }) {
  return (
    <div className="kv">
      <div className="kv-k">{label}</div>
      <div className="kv-v">{value ?? "—"}</div>
    </div>
  );
}

function Section({ title, children, right }) {
  return (
    <section className="card">
      <header className="card-h">
        <h2>{title}</h2>
        {right}
      </header>
      <div className="card-b">{children}</div>
    </section>
  );
}

const themes = [
  "HIGH_FIBRE",
  "HIGH_PROTEIN",
  "NORMAL",
  "LOW_CARB",
  "WATER_RETENTION",
];
const ethnicitys = [
  "NORTH Indian: Punjabi",
  "NORTH Indian: Kashmiri",
  "NORTH Indian: Amritsari food",
  "NORTH Indian: North Indian Generic",
  "NORTH Indian: Pahadi food",
  "WEST: Rajasthani",
  "WEST: Gujarati",
  "WEST: Goan",
  "WEST: Maharashtrian",
  "EAST Indian: Bengali",
  "EAST Indian: Odia",
  "North-Eastern Cuisine",
  "South Indian: Tamil",
  "South Indian: Telugu",
  "South Indian: Kannada",
  "South Indian: Kerala",
  "Central India: MP cuisine",
  "Central India: Chhattisgarhi cuisine",
  "Central India: Jharkhand cuisine",
  "Generic cuisine",
  "Italian",
  "Chinese",
  "French",
  "Mexican",
  "Japanese",
  "Thai",
  "Spanish",
  "Greek",
  "Turkish",
  "Korean",
  "Vietnamese",
  "Lebanese",
  "Brazilian",
  "Moroccan",
  "American",
  "Ethiopian",
  "German",
  "Russian",
  "Indonesian",
];
const API_BASE = "https://live-prod.production.fitelo.net";

function App() {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [theme, setTheme] = useState("");
  const [ethnicity, setEthanicity] = useState("");
  const [fluctuationKcal, setFluctuationKcal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [showRaw, setShowRaw] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setPlan(null);
    setError("");

    try {
      const res = await fetch(
        `${API_BASE}/diet/api/diet-engine-import/v4/diet-engine`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            phoneNumber,
            theme,
            ethnicity,
            fluctuation_kcal: Number(fluctuationKcal),
          }),
        }
      );
      if (!res.ok) throw new Error(res.statusText);
      const json = await res.json();
      if (!json?.data) throw new Error("Missing data in response");

      // The API key is spelled "dietEngineResposne" in your sample (typo kept!)
      setPlan(json.data);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch meal plan.");
    } finally {
      setLoading(false);
    }
  };

  const d = plan?.dietEngineResposne; // <- typo preserved to match API
  const payload = plan?.payload;

  return (
    <div className="App">
      <h1>Weekly Meal-Plan Generator</h1>

      <form onSubmit={handleSubmit} className="form">
        <label>
          Phone Number
          <input
            type="tel"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            required
          />
        </label>

        <label>
          Theme
          <select
            value={theme}
            onChange={(e) => setTheme(e.target.value)}
            required
          >
            <option value="" disabled>
              — select theme —
            </option>
            {themes.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
        <label>
          Ethnicity
          <select
            value={ethnicity}
            onChange={(e) => setEthanicity(e.target.value)}
            required
          >
            <option value="" disabled>
              — select Ethnicity —
            </option>
            {ethnicitys.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
        <label>
          Fluctuation Kcal
          <input
            type="number"
            // min="0"
            step="10"
            value={fluctuationKcal}
            onChange={(e) => setFluctuationKcal(Number(e.target.value || 0))}
            required
          />
        </label>

        <button type="submit" disabled={loading || !phoneNumber || !theme || !ethnicity}>
          {loading ? "Submitting…" : "Submit"}
        </button>
      </form>

      {loading && <div className="loader">Loading…</div>}
      {error && <div className="error">{error}</div>}

      {plan && (
        <div className="results">
          <button onClick={() => setShowRaw((v) => !v)} className="secondary">
            {showRaw ? "Hide Raw JSON" : "Show Raw JSON"}
          </button>

          {showRaw ? (
            <pre className="raw">{JSON.stringify(plan, null, 2)}</pre>
          ) : (
            <>
              {/* ===== Weekly Summary ===== */}
              <Section
                title="Weekly Summary"
                right={
                  payload?.theme ? (
                    <span className="pill">Theme: {payload.theme}</span>
                  ) : null
                }
              >
                <div className="grid two">
                  <KeyVal label="Seed" value={payload?.seed} />
                  <KeyVal
                    label="Fluctuation (kcal)"
                    value={payload?.fluctuation_kcal}
                  />
                  <KeyVal
                    label="Recall: Total Meals"
                    value={payload?.recall?.total_meals}
                  />
                  <KeyVal
                    label="Meal Pattern"
                    value={d?.days?.[0]?.selected_meal_pattern || "—"}
                  />
                </div>
                <div className="muted">
                  7-day plan with detailed meals, reasons, warnings, and user
                  payload below.
                </div>
              </Section>

              {/* ===== Weekly Warnings ===== */}
              {Array.isArray(d?.weekly_warnings) &&
                d.weekly_warnings.length > 0 && (
                  <Section title="Weekly Warnings">
                    <ul className="list">
                      {d.weekly_warnings.map((w, i) => (
                        <li key={i}>{w}</li>
                      ))}
                    </ul>
                  </Section>
                )}

              {/* ===== Days (Detailed) ===== */}
              {Array.isArray(d?.days) &&
                d.days.map((day, idx) => {
                  const tc = day?.theme_checks;
                  const hasTC = !!tc && Object.keys(tc).length > 0;
                  const status = hasTC ? (
                    typeof tc.success === "boolean" ? (
                      <Badge ok={tc.success} />
                    ) : (
                      <span className="muted">Not available</span>
                    )
                  ) : (
                    <span className="muted">Not available</span>
                  );

                  return (
                    <Section
                      key={idx}
                      title={`Day ${idx + 1}`}
                      right={
                        <div className="row">
                          {status}
                          {hasTC && (tc.theme || tc.actual) ? (
                            <span className="muted small">
                              {tc.theme ? `${tc.theme}` : ""}
                              {tc.theme && tc.actual ? " • " : ""}
                              {tc.actual || ""}
                            </span>
                          ) : (
                            <span className="muted small">
                              Theme check not provided
                            </span>
                          )}
                        </div>
                      }
                    >
                      <div className="grid three">
                        <KeyVal label="TDEE" value={day?.tdee} />
                        <KeyVal
                          label="Fluctuation (kcal)"
                          value={day?.fluctuation_kcal}
                        />
                        <KeyVal
                          label="Total Target (kcal)"
                          value={day?.total_kcal_target}
                        />
                      </div>

                      <div className="sub">
                        <strong>Selected Meal Pattern:</strong>{" "}
                        {day?.selected_meal_pattern || "—"}
                      </div>

                      <div className="table-wrap">
                        <table>
                          <thead>
                            <tr>
                              <th>Meal</th>
                              <th>Kcal Target</th>
                              <th>Kcal Actual</th>
                              <th>
                                Recipes (name • portion • partner • add-on)
                              </th>
                              <th>Reasons</th>
                              <th>Warnings</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(day?.meals || []).map((m, i2) => (
                              <tr key={`${m.meal_name}-${i2}`}>
                                <td>
                                  {String(m.meal_name || "").replace(/_/g, " ")}
                                </td>
                                <td>{m?.kcal_target ?? "—"}</td>
                                <td>{m?.kcal_actual ?? "—"}</td>
                                <td>
                                  {(m?.recipes || []).length ? (
                                    <ul className="tight">
                                      {m.recipes.map((r, i3) => (
                                        <li key={i3}>
                                          <span className="mono">
                                            {r?.name || "—"}
                                          </span>
                                          {typeof r?.portion === "number" && (
                                            <> • portion: {r.portion}</>
                                          )}
                                          {" • partner: "}
                                          {String(r?.is_partner ?? false)}
                                          {" • add-on: "}
                                          {String(r?.add_on ?? false)}
                                        </li>
                                      ))}
                                    </ul>
                                  ) : (
                                    "—"
                                  )}
                                </td>
                                <td>
                                  {(m?.reasons || []).length ? (
                                    <ul className="tight">
                                      {m.reasons.map((x, i4) => (
                                        <li key={i4}>{x}</li>
                                      ))}
                                    </ul>
                                  ) : (
                                    "—"
                                  )}
                                </td>
                                <td>
                                  {(m?.warnings || []).length ? (
                                    <ul className="tight warn">
                                      {m.warnings.map((x, i5) => (
                                        <li key={i5}>{x}</li>
                                      ))}
                                    </ul>
                                  ) : (
                                    "—"
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Theme check details block */}
                      {hasTC &&
                        (tc.theme ||
                          tc.goal ||
                          tc.actual ||
                          typeof tc.success === "boolean") && (
                          <div className="theme-check">
                            <div>
                              <strong>Theme:</strong> {tc.theme ?? "—"}
                            </div>
                            <div>
                              <strong>Goal:</strong> {tc.goal ?? "—"}
                            </div>
                            <div>
                              <strong>Actual:</strong> {tc.actual ?? "—"}
                            </div>
                            <div>
                              <strong>Status:</strong>{" "}
                              {typeof tc.success === "boolean" ? (
                                <Badge ok={tc.success} />
                              ) : (
                                <span className="muted">Not available</span>
                              )}
                            </div>
                          </div>
                        )}

                      {(day?.meal_split_inconsistencies || []).length > 0 && (
                        <div className="warnbox">
                          <strong>Meal Split Inconsistencies</strong>
                          <ul className="tight">
                            {day.meal_split_inconsistencies.map((x, i6) => (
                              <li key={i6}>{x}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </Section>
                  );
                })}

              {/* ===== Payload (User Inputs) ===== */}
              <Section title="Payload (User Inputs)">
                <div className="grid three">
                  <KeyVal label="Age" value={payload?.age} />
                  <KeyVal label="Gender" value={payload?.gender} />
                  <KeyVal label="Height (cm)" value={payload?.height_cm} />
                  <KeyVal label="Weight (kg)" value={payload?.weight_kg} />
                  <KeyVal
                    label="Activity Level"
                    value={payload?.activity_level}
                  />
                  <KeyVal
                    label="Diet Pref"
                    value={payload?.preferences?.diet_pref}
                  />
                </div>

                <h3>Counselling</h3>
                <div className="grid three">
                  <KeyVal
                    label="Living With"
                    value={payload?.counselling?.living_with}
                  />
                  <KeyVal
                    label="Who Cooks"
                    value={payload?.counselling?.who_cooks}
                  />
                  <KeyVal
                    label="Kitchen Equipment Level"
                    value={payload?.counselling?.kitchen_equipment_level}
                  />
                  <KeyVal
                    label="Cooking Preference"
                    value={payload?.counselling?.cooking_preference}
                  />
                  <KeyVal
                    label="Occupation"
                    value={payload?.counselling?.occupation}
                  />
                  <KeyVal
                    label="Working Style"
                    value={payload?.counselling?.job_working_style}
                  />
                  <KeyVal
                    label="Work Timings"
                    value={payload?.counselling?.work_timings}
                  />
                </div>

                <div className="sub">Kitchen Equipment Items</div>
                <ul className="list">
                  {(payload?.counselling?.kitchen_equipment_items || []).map(
                    (x, i) => (
                      <li key={i}>{x}</li>
                    )
                  )}
                </ul>

                <div className="grid three">
                  <KeyVal
                    label="Taste Pref"
                    value={
                      payload?.counselling?.eating_habits?.taste_pref || "—"
                    }
                  />
                  <KeyVal
                    label="Ethnicity"
                    value={
                      payload?.counselling?.eating_habits?.ethnicity || "—"
                    }
                  />
                  <KeyVal
                    label="Behaviour"
                    value={
                      payload?.counselling?.eating_habits?.behaviour || "—"
                    }
                  />
                </div>

                <h4>Clinical</h4>
                <div className="grid three">
                  <KeyVal
                    label="Medical Conditions"
                    value={
                      (
                        payload?.counselling?.clinical?.medical_conditions || []
                      ).join(", ") || "—"
                    }
                  />
                  <KeyVal
                    label="Symptoms"
                    value={
                      (payload?.counselling?.clinical?.symptoms || []).join(
                        ", "
                      ) || "—"
                    }
                  />
                  <KeyVal
                    label="Deficiencies/Toxicities"
                    value={
                      (
                        payload?.counselling?.clinical
                          ?.deficiencies_toxicities || []
                      ).join(", ") || "—"
                    }
                  />
                  <KeyVal
                    label="Therapies"
                    value={
                      (payload?.counselling?.clinical?.therapies || []).join(
                        ", "
                      ) || "—"
                    }
                  />
                  <KeyVal
                    label="Supplements"
                    value={
                      (payload?.counselling?.clinical?.supplements || []).join(
                        ", "
                      ) || "—"
                    }
                  />
                  <KeyVal
                    label="Surgeries"
                    value={
                      (payload?.counselling?.clinical?.surgeries || []).join(
                        ", "
                      ) || "—"
                    }
                  />
                </div>

                <h4>Current Location</h4>
                <div className="grid three">
                  <KeyVal
                    label="Country"
                    value={payload?.counselling?.current_location?.country}
                  />
                  <KeyVal
                    label="City"
                    value={payload?.counselling?.current_location?.city}
                  />
                  <KeyVal
                    label="Region"
                    value={payload?.counselling?.current_location?.region}
                  />
                  <KeyVal
                    label="Metro"
                    value={String(
                      payload?.counselling?.current_location?.metro ?? "—"
                    )}
                  />
                  <KeyVal
                    label="Feels Like (°C)"
                    value={payload?.counselling?.current_location?.feels_like_c}
                  />
                </div>

                <h3>Preferences</h3>
                <div className="grid three">
                  <KeyVal
                    label="Diet Pref"
                    value={payload?.preferences?.diet_pref}
                  />
                  <KeyVal
                    label="Thermogenic Pref"
                    value={payload?.preferences?.thermogenic_pref}
                  />
                  <KeyVal
                    label="Allergies/Intolerances"
                    value={
                      (payload?.preferences?.allergies_intolerances || []).join(
                        ", "
                      ) || "—"
                    }
                  />
                </div>

                <div className="grid two">
                  <KeyVal
                    label="Likes (Categories)"
                    value={
                      (payload?.preferences?.likes?.categories || []).join(
                        ", "
                      ) || "—"
                    }
                  />
                  <KeyVal
                    label="Dislikes (Ingredients)"
                    value={
                      (payload?.preferences?.dislikes?.ingredients || []).join(
                        ", "
                      ) || "—"
                    }
                  />
                </div>

                <h3>Goals</h3>
                <div className="grid two">
                  <KeyVal
                    label="Primary"
                    value={(payload?.goals?.primary || []).join(", ") || "—"}
                  />
                  <KeyVal
                    label="Secondary"
                    value={(payload?.goals?.secondary || []).join(", ") || "—"}
                  />
                </div>

                <h3>Recall & FFQ</h3>
                <div className="grid three">
                  <KeyVal
                    label="Total Meals"
                    value={payload?.recall?.total_meals}
                  />
                  <KeyVal label="Mains" value={payload?.recall?.mains} />
                  <KeyVal label="Mids" value={payload?.recall?.mids} />
                </div>
                <div className="sub">FFQ Entries</div>
                {(payload?.ffq?.entries || []).length ? (
                  <ul className="list">
                    {payload.ffq.entries.map((x, i) => (
                      <li key={i}>{JSON.stringify(x)}</li>
                    ))}
                  </ul>
                ) : (
                  <div className="muted">No FFQ entries provided.</div>
                )}

                <h3>Previous Diet</h3>
                {(payload?.previousDiet || []).length ? (
                  <ul className="list">
                    {payload.previousDiet.map((x, i) => (
                      <li key={i}>{JSON.stringify(x)}</li>
                    ))}
                  </ul>
                ) : (
                  <div className="muted">No previous diet records.</div>
                )}
              </Section>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default App;
